declare var ol: any;

export interface SiblingPolygon {
  id: string | number;
  polygon: string; // WKT format
  color?: string;
  label?: string;
}

export class SiblingPolygonManager {
  private siblingFeatures: Map<string | number, any> = new Map();
  private siblingSource: any = null;
  private siblingLayer: any = null;
  private defaultColor = 'rgba(255, 165, 0, 0.3)'; // Orange transparent
  private defaultBorderColor = '#ff8c00';

  constructor(
    private getMap: () => any,
    private parsePolygonJson: (json: string) => [number, number][] | null
  ) {}

  get hasSiblings(): boolean {
    return this.siblingFeatures.size > 0;
  }

  /**
   * Initialise la couche pour les polygones voisins
   */
  private initLayer(): void {
    const map = this.getMap();
    if (!map || this.siblingLayer) return;

    this.siblingSource = new ol.source.Vector();

    this.siblingLayer = new ol.layer.Vector({
      source: this.siblingSource,
      zIndex: 1, // Au-dessus du parent mais en dessous du polygone principal
      style: (feature: any) => {
        const color = feature.get('fillColor') || this.defaultColor;
        const borderColor = feature.get('borderColor') || this.defaultBorderColor;
        const label = feature.get('label');

        const styles = [
          new ol.style.Style({
            stroke: new ol.style.Stroke({
              color: borderColor,
              width: 2,
              lineDash: [5, 5]
            }),
            fill: new ol.style.Fill({ color: color })
          })
        ];

        // Ajouter le label si présent
        if (label) {
          const geometry = feature.getGeometry();
          const center = ol.extent.getCenter(geometry.getExtent());

          styles.push(new ol.style.Style({
            geometry: new ol.geom.Point(center),
            text: new ol.style.Text({
              text: label,
              font: 'bold 12px Arial',
              fill: new ol.style.Fill({ color: '#333' }),
              stroke: new ol.style.Stroke({ color: '#fff', width: 3 }),
              overflow: true
            })
          }));
        }

        return styles;
      }
    });

    map.addLayer(this.siblingLayer);
  }

  /**
   * Ajoute un polygone voisin
   */
  addSiblingPolygon(sibling: SiblingPolygon): void {
    this.initLayer();

    const polygonCoordinates = this.parsePolygonJson(sibling.polygon);
    if (!polygonCoordinates || polygonCoordinates.length < 3) {
      console.error('Invalid sibling polygon coordinates');
      return;
    }

    try {
      const firstCoord = polygonCoordinates[0];
      const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

      const transformedRing = isProjected
        ? polygonCoordinates
        : polygonCoordinates.map(coord => ol.proj.fromLonLat(coord));

      const polygonGeometry = new ol.geom.Polygon([transformedRing]);

      const feature = new ol.Feature({
        geometry: polygonGeometry,
        type: 'siblingPolygon',
        siblingId: sibling.id,
        label: sibling.label,
        fillColor: sibling.color || this.defaultColor,
        borderColor: this.getBorderColor(sibling.color)
      });

      // Supprimer l'ancien si existe
      if (this.siblingFeatures.has(sibling.id)) {
        const oldFeature = this.siblingFeatures.get(sibling.id);
        this.siblingSource.removeFeature(oldFeature);
      }

      this.siblingSource.addFeature(feature);
      this.siblingFeatures.set(sibling.id, feature);

    } catch (error) {
      console.error('Error adding sibling polygon:', error);
    }
  }

  /**
   * Ajoute plusieurs polygones voisins
   */
  addSiblingPolygons(siblings: SiblingPolygon[], center?:boolean): void {
    if (!siblings || siblings.length === 0) return;

    siblings.forEach(sibling => this.addSiblingPolygon(sibling));
    if (center) {
      this.fitToAllSiblings();
    }

  }

  /**
   * Supprime un polygone voisin par son ID
   */
  removeSiblingPolygon(id: string | number): void {
    if (!this.siblingFeatures.has(id)) return;

    const feature = this.siblingFeatures.get(id);
    if (this.siblingSource && feature) {
      this.siblingSource.removeFeature(feature);
    }
    this.siblingFeatures.delete(id);
  }

  /**
   * Efface tous les polygones voisins
   */
  clearAllSiblings(): void {
    if (this.siblingSource) {
      this.siblingSource.clear();
    }
    this.siblingFeatures.clear();
  }

  /**
   * Returns all sibling polygon coordinates as lon/lat rings.
   * Each entry is an array of [lon, lat][] (outer ring).
   */
  getAllSiblingCoordinatesLonLat(): [number, number][][] {
    const result: [number, number][][] = [];
    for (const [, feature] of this.siblingFeatures) {
      const geometry = feature.getGeometry();
      if (!geometry) continue;
      const projectedCoords: number[][] = geometry.getCoordinates()[0];
      const lonLatCoords = projectedCoords.map((c: number[]) => {
        const ll = ol.proj.toLonLat(c);
        return [ll[0], ll[1]] as [number, number];
      });
      result.push(lonLatCoords);
    }
    return result;
  }

  /**
   * Met à jour la couleur d'un polygone voisin
   */
  updateSiblingColor(id: string | number, color: string): void {
    const feature = this.siblingFeatures.get(id);
    if (!feature) return;

    feature.set('fillColor', color);
    feature.set('borderColor', this.getBorderColor(color));
    feature.changed(); // Force le rafraîchissement du style
  }

  /**
   * Vérifie si un point est à l'intérieur d'un polygone voisin.
   * Uses a tolerance to ignore points that lie exactly on the boundary (shared edges).
   */
  isPointInsideSibling(coordinate: number[]): { inside: boolean; siblingId?: string | number } {
    for (const [id, feature] of this.siblingFeatures) {
      const geometry = feature.getGeometry();
      if (this._isPointStrictlyInside(coordinate, geometry)) {
        return { inside: true, siblingId: id };
      }
    }
    return { inside: false };
  }

  /**
   * Check if a point is strictly inside a polygon (not merely on its boundary).
   * We test a small set of offsets around the point; only if ALL are inside do we consider it truly inside.
   * This prevents shared-edge points from being flagged.
   */
  private _isPointStrictlyInside(coordinate: number[], geometry: any): boolean {
    if (!geometry.intersectsCoordinate(coordinate)) {
      return false;
    }

    // Tolerance in map units (projected metres). ~0.1 m is enough to skip shared edges.
    const tol = 0.1;
    const offsets = [
      [tol, 0], [-tol, 0], [0, tol], [0, -tol]
    ];

    // If any tiny offset falls outside, the original point is on/very near the boundary → skip.
    for (const off of offsets) {
      const shifted: number[] = [coordinate[0] + off[0], coordinate[1] + off[1]];
      if (!geometry.intersectsCoordinate(shifted)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Vérifie si un polygone chevauche un polygone voisin
   * Vérifie : 1) si les points sont à l'intérieur, 2) si les segments se croisent
   * Uses tolerance to ignore shared edges (e.g. after a split).
   */
  doesPolygonOverlapSibling(polygonGeometry: any): { overlaps: boolean; siblingIds: (string | number)[] } {
    const overlappingIds: (string | number)[] = [];

    for (const [id, feature] of this.siblingFeatures) {
      const siblingGeometry = feature.getGeometry();
      const siblingExtent = siblingGeometry.getExtent();
      const polygonExtent = polygonGeometry.getExtent();

      // Vérification rapide avec les extents
      if (!ol.extent.intersects(siblingExtent, polygonExtent)) {
        continue;
      }

      const polygonCoords = polygonGeometry.getCoordinates()[0];
      const siblingCoords = siblingGeometry.getCoordinates()[0];
      let hasOverlap = false;

      // 1. Vérifier si un point du polygone est strictement dans le sibling (with tolerance)
      for (const coord of polygonCoords) {
        if (this._isPointStrictlyInside(coord, siblingGeometry)) {
          hasOverlap = true;
          break;
        }
      }

      // 2. Vérifier si un point du sibling est strictement dans le polygone (with tolerance)
      if (!hasOverlap) {
        for (const coord of siblingCoords) {
          if (this._isPointStrictlyInside(coord, polygonGeometry)) {
            hasOverlap = true;
            break;
          }
        }
      }

      // 3. Vérifier si les segments se croisent (skip collinear / shared-edge segments)
      if (!hasOverlap) {
        hasOverlap = this.doPolygonSegmentsIntersect(polygonCoords, siblingCoords);
      }

      if (hasOverlap) {
        overlappingIds.push(id);
      }
    }

    return {
      overlaps: overlappingIds.length > 0,
      siblingIds: overlappingIds
    };
  }

  /**
   * Vérifie si deux segments se croisent.
   * Ignores shared endpoints and collinear (overlapping) segments – these occur on shared split edges.
   */
  private doSegmentsIntersect(p1: number[], p2: number[], p3: number[], p4: number[]): boolean {
    const ccw = (A: number[], B: number[], C: number[]): boolean => {
      return (C[1] - A[1]) * (B[0] - A[0]) > (B[1] - A[1]) * (C[0] - A[0]);
    };

    // Vérifier si les segments partagent un point (adjacents) – use tolerance for floating-point
    const eps = 1e-6;
    const sharePoint = (
      (Math.abs(p1[0] - p3[0]) < eps && Math.abs(p1[1] - p3[1]) < eps) ||
      (Math.abs(p1[0] - p4[0]) < eps && Math.abs(p1[1] - p4[1]) < eps) ||
      (Math.abs(p2[0] - p3[0]) < eps && Math.abs(p2[1] - p3[1]) < eps) ||
      (Math.abs(p2[0] - p4[0]) < eps && Math.abs(p2[1] - p4[1]) < eps)
    );

    if (sharePoint) {
      return false;
    }

    // Check if segments are collinear (shared edge from split). Cross product ≈ 0 for all combos.
    const cross = (A: number[], B: number[], C: number[]): number =>
      (B[0] - A[0]) * (C[1] - A[1]) - (B[1] - A[1]) * (C[0] - A[0]);

    if (Math.abs(cross(p1, p2, p3)) < eps && Math.abs(cross(p1, p2, p4)) < eps) {
      // Segments are collinear – treat as shared edge, not a real intersection
      return false;
    }

    return (
      ccw(p1, p3, p4) !== ccw(p2, p3, p4) &&
      ccw(p1, p2, p3) !== ccw(p1, p2, p4)
    );
  }

  /**
   * Vérifie si les segments de deux polygones se croisent
   */
  private doPolygonSegmentsIntersect(coords1: number[][], coords2: number[][]): boolean {
    const n1 = coords1.length;
    const n2 = coords2.length;

    for (let i = 0; i < n1 - 1; i++) {
      for (let j = 0; j < n2 - 1; j++) {
        if (this.doSegmentsIntersect(coords1[i], coords1[i + 1], coords2[j], coords2[j + 1])) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Ajuste la vue pour montrer tous les polygones voisins
   */
  fitToAllSiblings(): void {
    const map = this.getMap();
    if (!map || this.siblingFeatures.size === 0) return;

    const allExtents: any[] = [];

    this.siblingFeatures.forEach((feature) => {
      const extent = feature.getGeometry().getExtent();
      if (extent && !extent.some((val: any) => !isFinite(val))) {
        allExtents.push(extent);
      }
    });

    if (allExtents.length === 0) return;

    const combinedExtent = allExtents.reduce((acc, extent) => {
      return [
        Math.min(acc[0], extent[0]),
        Math.min(acc[1], extent[1]),
        Math.max(acc[2], extent[2]),
        Math.max(acc[3], extent[3])
      ];
    }, allExtents[0]);

    map.getView().fit(combinedExtent, {
      padding: [50, 50, 50, 50],
      maxZoom: 17,
      duration: 500
    });
  }

  /**
   * Récupère tous les IDs des polygones voisins
   */
  getSiblingIds(): (string | number)[] {
    return Array.from(this.siblingFeatures.keys());
  }

  /**
   * Supprime la couche du map
   */
  destroy(): void {
    const map = this.getMap();
    if (map && this.siblingLayer) {
      map.removeLayer(this.siblingLayer);
    }
    this.siblingLayer = null;
    this.siblingSource = null;
    this.siblingFeatures.clear();
  }

  private getBorderColor(fillColor?: string): string {
    if (!fillColor) return this.defaultBorderColor;

    // Extraire les composants RGB et créer une couleur plus foncée
    const match = fillColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const r = Math.max(0, parseInt(match[1]) - 50);
      const g = Math.max(0, parseInt(match[2]) - 50);
      const b = Math.max(0, parseInt(match[3]) - 50);
      return `rgb(${r}, ${g}, ${b})`;
    }

    return this.defaultBorderColor;
  }
}

